import { getSupabaseClient } from '../../lib/supabase'
import type { Database } from '../../types/database.generated'

export type SecuritySettings = Database['public']['Tables']['organization_security_settings']['Row']
export type SecurityAuditEvent = Database['public']['Tables']['security_audit_events']['Row']

export async function getSecurityWorkspace(organizationId: string, includeAudit: boolean) {
  const supabase = getSupabaseClient()
  const settings = await supabase.from('organization_security_settings').select('*').eq('organization_id', organizationId).single()
  if (settings.error) throw settings.error
  let events: SecurityAuditEvent[] = []
  if (includeAudit) {
    const audit = await supabase.from('security_audit_events').select('*').eq('organization_id', organizationId).order('occurred_at', { ascending: false }).limit(100)
    if (audit.error) throw audit.error
    events = audit.data
  }
  return { settings: settings.data, events }
}

export async function updateSecuritySettings({ organizationId, values }: { organizationId: string; values: Pick<SecuritySettings, 'data_retention_days' | 'require_mfa_for_admins' | 'security_contact_email' | 'incident_response_acknowledged_at' | 'privacy_policy_acknowledged_at'> }) {
  const { error } = await getSupabaseClient().from('organization_security_settings').update(values).eq('organization_id', organizationId)
  if (error) throw error
}

export function securityReadiness(settings: SecuritySettings, isAdmin: boolean) {
  return [
    { label: 'Tenant isolation and RLS', complete: true, detail: 'Database policies restrict every organization resource.' },
    { label: 'Immutable audit trail', complete: isAdmin, detail: isAdmin ? 'Owner/Admin audit visibility is enabled.' : 'Owner or Admin access is required.' },
    { label: 'Administrative MFA policy', complete: settings.require_mfa_for_admins, detail: 'Owners and administrators must use MFA before production launch.' },
    { label: 'Security contact', complete: Boolean(settings.security_contact_email), detail: settings.security_contact_email ?? 'Add a monitored security email address.' },
    { label: 'Incident response plan', complete: Boolean(settings.incident_response_acknowledged_at), detail: 'Document detection, containment, recovery and notification ownership.' },
    { label: 'Privacy and retention review', complete: Boolean(settings.privacy_policy_acknowledged_at), detail: `${settings.data_retention_days} day retention policy.` },
  ]
}
